import React, { useEffect, useState, useRef } from 'react';
import {
    Box, Stack, Grid, FormControl,
    CircularProgress, Button, Alert, Snackbar, TextField,
    Paper, Typography, IconButton, Divider, Autocomplete,
    Dialog, DialogTitle, DialogActions, DialogContent, Checkbox, FormControlLabel
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../contexts/auth.context';
import { useEvent } from '../../contexts/event.context';
import api from '../../services/api';
import * as qs from 'qs';
import { maskCPF2, maskCNPJ } from '../../utils/mask';
import { downloadBase64File, isRawLabelPrintingEnvironment } from '../../utils/labelDownload';

export default function Crachas() {
    const { basePath } = useAuth();
    const { events, getEvents, pagination } = useEvent();
    const [loading, setLoading] = useState(false);
    const [subscribers, setSubscribers] = useState([]);
    const [searchCpf, setSearchCpf] = useState('');
    const [searchName, setSearchName] = useState('');
    const [searchCnpj, setSearchCnpj] = useState('');
    const [searchCompanyName, setSearchCompanyName] = useState('');
    const [showOnlyPrinted, setShowOnlyPrinted] = useState(false);
    const [stateAlert, setStateAlert] = useState({ status: false, type: 'success', msg: '' });
    const [printingId, setPrintingId] = useState(null);
    const [eventSelected, setEventSelected] = useState(null);
    const [eventSelectedId, setEventSelectedId] = useState(null);
    const [eventOptions, setEventOptions] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [hasMoreEvents, setHasMoreEvents] = useState(true);
    const pageEvents = useRef(1);
    const eventIdsRef = useRef(new Set());
    const [eventSearchText, setEventSearchText] = useState('');
    const eventSearchTimer = useRef(null);
    const [openPrintModal, setOpenPrintModal] = useState(false);
    const [pdfData, setPdfData] = useState(null);
    const printIframeRef = useRef(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [printingBatch, setPrintingBatch] = useState(false);
    const [copies, setCopies] = useState(1);
    const [printer, setPrinter] = useState('ZEBRA');
    const [registeringFrequency, setRegisteringFrequency] = useState(false);
    const [registeredIds, setRegisteredIds] = useState(new Set());
    const [openAuthorizationModal, setOpenAuthorizationModal] = useState(false);
    const [adminCodeInput, setAdminCodeInput] = useState('');
    const [configuredAdminCode, setConfiguredAdminCode] = useState('');
    const [pendingPrintId, setPendingPrintId] = useState(null);
    const [authorizationError, setAuthorizationError] = useState('');
    const [openManageCodeModal, setOpenManageCodeModal] = useState(false);
    const [currentCodeInput, setCurrentCodeInput] = useState('');
    const [newCodeInput, setNewCodeInput] = useState('');
    const [confirmCodeInput, setConfirmCodeInput] = useState('');
    const [savingCode, setSavingCode] = useState(false);
    const [isAuthenticatedRoleUser, setIsAuthenticatedRoleUser] = useState(false);

    const ADMIN_CODE_FIELD = 'reprintAuthorizationCode';

    function normalizeSearchText(value) {
        return String(value || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLocaleLowerCase('pt-BR')
            .trim();
    }

    async function findCompanyIdsByName(companyName) {
        const normalizedSearch = normalizeSearchText(companyName);
        const pageSize = 100;
        let page = 1;
        let pageCount = 1;
        const matchingIds = [];

        while (page <= pageCount) {
            const query = qs.stringify({
                fields: ['social_reason', 'trade'],
                pagination: { page, pageSize },
                encodeValuesOnly: true,
            });
            const response = await api.get(`/companies?${query}`);
            const companies = response.data?.data || [];

            companies.forEach(company => {
                const attributes = company.attributes || {};
                const companyNames = [attributes.social_reason, attributes.trade]
                    .map(normalizeSearchText);

                if (companyNames.some(name => name.includes(normalizedSearch))) {
                    matchingIds.push(company.id);
                }
            });

            pageCount = response.data?.meta?.pagination?.pageCount || page;
            page += 1;
        }

        return matchingIds;
    }

    async function loadCurrentUserRole() {
        try {
            const response = await api.get('/users/me?populate=*');
            const userData = response?.data || {};
            const roleType =
                userData?.role?.type ||
                userData?.role?.data?.attributes?.type ||
                userData?.role?.name ||
                '';

            setIsAuthenticatedRoleUser(String(roleType).toLowerCase() === 'authenticated');
        } catch (error) {
            console.error('Erro ao carregar role do usuario logado:', error);
            setIsAuthenticatedRoleUser(false);
        }
    }

    async function handleSearch() {
        if (!eventSelected) {
            setStateAlert({ status: true, type: 'warning', msg: 'Por favor, selecione um evento primeiro.' });
            return;
        }

        setLoading(true);
        
        try {
            // Construir filtros dinâmicos
            let filters = {
                deleted: { $eq: false },
                event: { name: eventSelected }
            };

            // Filtros de subscribers
            let subscriberFilters = {};

            if (searchCpf) {
                subscriberFilters.document = { $contains: searchCpf };
            }

            if (searchName) {
                subscriberFilters.name = { $containsi: searchName };
            }

            if (showOnlyPrinted) {
                subscriberFilters.printed = { $eq: true };
            }

            // Filtros de empresa
            let companyFilters = {};
            
            if (searchCnpj) {
                companyFilters.document = { $contains: searchCnpj };
            }
            
            if (searchCompanyName) {
                const matchingCompanyIds = await findCompanyIdsByName(searchCompanyName);
                companyFilters.$or = [
                    { id: { $in: matchingCompanyIds } }
                ];
            }

            // Adicionar filtros se existirem
            if (Object.keys(subscriberFilters).length > 0) {
                filters.subscribers = subscriberFilters;
            }

            if (Object.keys(companyFilters).length > 0) {
                filters.company = companyFilters;
            }

            const params = {
                sort: ['createdAt:desc'],
                pagination: { page: 1, pageSize: 150 },
                filters: filters,
                populate: {
                    event: { fields: ['name'] },
                    plan: { fields: ['type'] },
                    company: { fields: ['city', 'state', 'social_reason', 'trade', 'document'] },
                    subscribers: { fields: ['id', 'name', 'badge', 'document', 'whatsapp', 'email', 'city', 'state', 'printed', 'printedAt'] }
                },
                fields: ['status'],
                encodeValuesOnly: true
            };

            const query = qs.stringify(params, {
                skipNulls: false,
                skipUndefined: false,
                skipEmptyStrings: true
            });

            const response = await api.get(`/orders/?${query}`);
            const ordersData = response.data.data;

            if (!ordersData || ordersData.length === 0) {
                setSubscribers([]);
                setStateAlert({ status: true, type: 'info', msg: 'Nenhum inscrito encontrado com os critérios informados.' });
                setLoading(false);
                return;
            }

            // Transformar dados para exibição
            const subscribersData = ordersData.flatMap(order => {
                const company = order.attributes.company?.data?.attributes || {};
                const event = order.attributes.event?.data?.attributes?.name || 'N/A';
                const plan = order.attributes.plan?.data?.attributes?.type || 'N/A';
                const orderStatus = order.attributes.status || '';
                
                return order.attributes.subscribers.data.map(subscriber => {
                    const attributes = subscriber.attributes;
                    const subscriberName = String(attributes.name || '').trim();
                    const badge = String(attributes.badge || '').trim() || subscriberName.split(/\s+/)[0] || 'N/A';

                    return ({
                    id: subscriber.id,
                    name: subscriberName || 'N/A',
                    badge,
                    document: maskCPF2(attributes.document) || 'N/A',
                    email: attributes.email || 'N/A',
                    whatsapp: attributes.whatsapp || 'N/A',
                    company: company.social_reason || company.trade || 'N/A',
                    document: maskCNPJ(company.document) || 'N/A',
                    city: company.city || attributes.city || 'N/A',
                    state: company.state || attributes.state || 'N/A',
                    event: event,
                    category: plan,
                    printed: attributes.printed || false,
                    printedAt: attributes.printedAt || null,
                    orderStatus: orderStatus,
                    });
                });
            }).sort((firstSubscriber, secondSubscriber) => {
                const companyComparison = firstSubscriber.company.localeCompare(
                    secondSubscriber.company,
                    'pt-BR',
                    { sensitivity: 'base' }
                );

                if (companyComparison !== 0) return companyComparison;

                return firstSubscriber.name.localeCompare(
                    secondSubscriber.name,
                    'pt-BR',
                    { sensitivity: 'base' }
                );
            });

            setSubscribers(subscribersData);

            if (eventSelectedId && subscribersData.length > 0) {
                try {
                    const subscriberIds = subscribersData.map(s => s.id);
                    const freqParams = {
                        filters: {
                            event: { id: { $eq: eventSelectedId } },
                            subscriber: { id: { $in: subscriberIds } }
                        },
                        populate: { subscriber: { fields: ['id'] } },
                        pagination: { pageSize: 1000 },
                        encodeValuesOnly: true
                    };
                    const freqQuery = qs.stringify(freqParams, {
                        skipNulls: true,
                        skipUndefined: true,
                        skipEmptyStrings: true
                    });
                    const freqResponse = await api.get(`/frequencies?${freqQuery}`);
                    const registered = new Set();
                    freqResponse.data.data.forEach(item => {
                        const subscriberId = item.attributes?.subscriber?.data?.id || item.attributes?.subscriber;
                        if (subscriberId) {
                            registered.add(Number(subscriberId));
                        }
                    });
                    setRegisteredIds(registered);
                } catch (freqError) {
                    console.error('Erro ao verificar frequências:', freqError);
                    setRegisteredIds(new Set());
                }
            } else {
                setRegisteredIds(new Set());
            }

            setStateAlert({ status: true, type: 'success', msg: `${subscribersData.length} inscrito(s) encontrado(s).` });

        } catch (error) {
            console.error('Erro ao buscar inscritos:', error);
            setStateAlert({ status: true, type: 'error', msg: `Erro ao buscar inscritos: ${error.message}` });
            setSubscribers([]);
        } finally {
            setLoading(false);
        }
    }

    async function handlePrintLabel(subscriberId) {
        // Verifica se o crachá já foi impresso ou se a order está cancelada
        const subscriber = subscribers.find(s => s.id === subscriberId);
        
        if (subscriber && (subscriber.printed || subscriber.orderStatus === 'CANCELADA')) {
            // Se já foi impresso ou está cancelado, solicita codigo do administrador.
            setPendingPrintId(subscriberId);
            setAuthorizationError('');
            setAdminCodeInput('');
            setOpenAuthorizationModal(true);
            return;
        }
        
        const [printResult, frequencyResult] = await Promise.allSettled([
            printLabel(subscriberId),
            registerFrequencyForSubscriber(subscriberId)
        ]);

        if (frequencyResult.status === 'rejected' && printResult.status === 'fulfilled') {
            setStateAlert({
                status: true,
                type: 'warning',
                msg: 'Label processado, mas não foi possível registrar a frequência.'
            });
        }
    }

    async function printLabel(subscriberId) {
        setPrintingId(subscriberId);
        
        try {
            const copiesCount = Number(copies) || 1;
            const response = await api.post(`order/label`, {
                subscriber: subscriberId,
                copies: copiesCount,
                ...(isRawLabelPrintingEnvironment() ? { printer } : {}),
            });

            if (response.status === 200) {
                if (response.data.bat) {
                    downloadBase64File(response.data.bat, `etiqueta-${subscriberId}.bat`, 'application/octet-stream');
                    setStateAlert({ status: true, type: 'success', msg: 'Arquivo de impressão gerado.' });
                    await markAsPrinted(subscriberId);
                    return;
                }

                const base64Pdf = response.data.pdf;
                
                // Converter base64 para Blob para evitar problemas de CORS
                const byteCharacters = atob(base64Pdf);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                
                setPdfData(url);
                setOpenPrintModal(true);
                setStateAlert({ status: true, type: 'success', msg: 'Enviando para impressão...' });
                
                // Marcar como impresso no backend
                await markAsPrinted(subscriberId);
            }
        } catch (error) {
            console.error('Erro ao gerar crachá:', error);
            setStateAlert({ status: true, type: 'error', msg: `Erro ao gerar crachá: ${error.message}` });
        } finally {
            setPrintingId(null);
        }
    }

    async function markAsPrinted(subscriberId) {
        try {
            const printedAt = new Date().toISOString();
            await api.put(`/subscribers/${subscriberId}`, {
                data: {
                    printed: true,
                    printedAt
                }
            });
            
            // Atualizar o estado local
            setSubscribers(prev => prev.map(sub => 
                sub.id === subscriberId 
                    ? { ...sub, printed: true, printedAt }
                    : sub
            ));
        } catch (error) {
            console.error('Erro ao marcar como impresso:', error);
        }
    }

    async function markBatchAsPrinted(subscriberIds = []) {
        if (!Array.isArray(subscriberIds) || subscriberIds.length === 0) {
            return;
        }

        const printedAt = new Date().toISOString();

        try {
            await Promise.allSettled(
                subscriberIds.map(subscriberId =>
                    api.put(`/subscribers/${subscriberId}`, {
                        data: {
                            printed: true,
                            printedAt
                        }
                    })
                )
            );

            setSubscribers(prev =>
                prev.map(sub =>
                    subscriberIds.includes(sub.id)
                        ? { ...sub, printed: true, printedAt }
                        : sub
                )
            );
        } catch (error) {
            console.error('Erro ao marcar lotes como impressos:', error);
        }
    }

    function validateAdminCode() {
        if (!configuredAdminCode) {
            setAuthorizationError('Codigo de reimpressao nao configurado.');
            return;
        }

        if (adminCodeInput.trim() !== configuredAdminCode) {
            setAuthorizationError('Codigo de autorizacao invalido.');
            return;
        }

        setOpenAuthorizationModal(false);
        setAuthorizationError('');
        printLabel(pendingPrintId);
        setPendingPrintId(null);
    }

    function handleCancelAuthorizationModal() {
        setOpenAuthorizationModal(false);
        setAuthorizationError('');
        setAdminCodeInput('');
        setPendingPrintId(null);
    }

    async function loadAuthorizationCode() {
        try {
            const response = await api.get('/configs/1');
            const attributes = response?.data?.data?.attributes || {};
            const code = attributes[ADMIN_CODE_FIELD];
            setConfiguredAdminCode(typeof code === 'string' ? code.trim() : '');
        } catch (error) {
            console.error('Erro ao carregar codigo de autorizacao:', error);
            setConfiguredAdminCode('');
        }
    }

    async function handleSaveAuthorizationCode() {
        if (!isAuthenticatedRoleUser) {
            setStateAlert({ status: true, type: 'error', msg: 'Somente usuarios do tipo authenticated podem alterar este codigo.' });
            return;
        }

        const currentCode = currentCodeInput.trim();
        const nextCode = newCodeInput.trim();
        const confirmCode = confirmCodeInput.trim();

        if (!nextCode || nextCode.length < 4) {
            setStateAlert({ status: true, type: 'warning', msg: 'Informe um codigo com pelo menos 4 caracteres.' });
            return;
        }

        if (nextCode !== confirmCode) {
            setStateAlert({ status: true, type: 'warning', msg: 'A confirmacao do codigo nao confere.' });
            return;
        }

        if (configuredAdminCode && currentCode !== configuredAdminCode) {
            setStateAlert({ status: true, type: 'error', msg: 'Codigo atual invalido para atualizar autorizacao.' });
            return;
        }

        setSavingCode(true);
        try {
            await api.put('/configs/1', { data: { [ADMIN_CODE_FIELD]: nextCode } });

            setConfiguredAdminCode(nextCode);
            setOpenManageCodeModal(false);
            setCurrentCodeInput('');
            setNewCodeInput('');
            setConfirmCodeInput('');
            setStateAlert({
                status: true,
                type: 'success',
                msg: 'Codigo de autorizacao salvo com sucesso.'
            });
        } catch (error) {
            console.error('Erro ao salvar codigo de autorizacao:', error);
            setStateAlert({ status: true, type: 'error', msg: 'Erro ao salvar codigo de autorizacao em configs/1.' });
        } finally {
            setSavingCode(false);
        }
    }

    function handlePrint() {
        if (printIframeRef.current) {
            try {
                printIframeRef.current.contentWindow.print();
            } catch (error) {   
                console.error('Erro ao imprimir:', error);
                setStateAlert({ status: true, type: 'error', msg: 'Erro ao enviar para impressão' });
            }
        }
    }

    function handleCloseModal() {
        setOpenPrintModal(false);
        if (pdfData) {
            URL.revokeObjectURL(pdfData);
        }
        setPdfData(null);
    }

    // Efeito para imprimir automaticamente quando o PDF carregar
    useEffect(() => {
        if (openPrintModal && pdfData && printIframeRef.current) {
            const iframe = printIframeRef.current;
            
            const handleLoad = () => {
                setTimeout(() => {
                    try {
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();
                    } catch (error) {
                        console.error('Erro ao imprimir automaticamente:', error);
                    }
                }, 500);
            };

            iframe.addEventListener('load', handleLoad);
            
            return () => {
                iframe.removeEventListener('load', handleLoad);
            };
        }
    }, [openPrintModal, pdfData]);

    function handleClearFilters() {
        setSearchCpf('');
        setSearchName('');
        setSearchCnpj('');
        setSearchCompanyName('');
        setShowOnlyPrinted(false);
        setSubscribers([]);
        setSelectedIds([]);
        setRegisteredIds(new Set());
    }

    function handleCopiesChange(value) {
        const numericValue = String(value).replace(/\D/g, '');
        if (!numericValue) {
            setCopies('');
            return;
        }

        setCopies(Math.min(20, Math.max(1, Number(numericValue))));
    }

    function handleToggleSelect(id) {
        setSelectedIds(prev => 
            prev.includes(id) 
                ? prev.filter(selectedId => selectedId !== id)
                : [...prev, id]
        );
    }

    function handleSelectAll() {
        setSelectedIds(subscribers.map(sub => sub.id));
    }

    function handleDeselectAll() {
        setSelectedIds([]);
    }

    async function registerFrequencyForSubscriber(subscriberId) {
        if (!eventSelectedId) {
            throw new Error('Evento não selecionado.');
        }

        const response = await api.post('/frequency/reader', {
            data: {
                event: eventSelectedId,
                id: subscriberId,
                readAt: new Date().toISOString()
            }
        });

        if (response.status !== 200) {
            throw new Error('Frequência não registrada.');
        }

        setRegisteredIds(prev => {
            const next = new Set(prev);
            next.add(subscriberId);
            return next;
        });
    }

    async function registerFrequencyForSelected() {
        if (selectedIds.length === 0) {
            return { successIds: [], failedIds: [] };
        }

        if (!eventSelectedId) {
            return { successIds: [], failedIds: selectedIds };
        }

        const readAt = new Date().toISOString();
        const results = await Promise.allSettled(
            selectedIds.map(id =>
                api.post('/frequency/reader', {
                    data: {
                        event: eventSelectedId,
                        id,
                        readAt
                    }
                })
            )
        );

        const successIds = [];
        const failedIds = [];

        results.forEach((result, index) => {
            if (result.status === 'fulfilled' && result.value.status === 200) {
                successIds.push(selectedIds[index]);
            } else {
                failedIds.push(selectedIds[index]);
            }
        });

        setRegisteredIds(prev => {
            const next = new Set(prev);
            successIds.forEach(id => next.add(id));
            return next;
        });

        return { successIds, failedIds };
    }

    async function handleRegisterFrequency() {
        if (selectedIds.length === 0) {
            setStateAlert({ status: true, type: 'warning', msg: 'Selecione ao menos um inscrito para registrar frequência.' });
            return;
        }

        if (!eventSelectedId) {
            setStateAlert({ status: true, type: 'warning', msg: 'Selecione um evento para registrar frequência.' });
            return;
        }

        setRegisteringFrequency(true);
        try {
            const { successIds, failedIds } = await registerFrequencyForSelected();
            if (failedIds.length === 0) {
                setStateAlert({ status: true, type: 'success', msg: `${successIds.length} frequência(s) registrada(s) com sucesso.` });
            } else {
                setStateAlert({ status: true, type: 'warning', msg: `${successIds.length} registrada(s), ${failedIds.length} falha(s).` });
            }
        } catch (error) {
            console.error('Erro ao registrar frequências:', error);
            setStateAlert({ status: true, type: 'error', msg: 'Erro ao registrar frequências.' });
        } finally {
            setRegisteringFrequency(false);
        }
    }

    async function handlePrintAndRegisterFrequency() {
        if (selectedIds.length === 0) {
            setStateAlert({ status: true, type: 'warning', msg: 'Selecione ao menos um inscrito para imprimir.' });
            return;
        }

        if (!eventSelectedId) {
            setStateAlert({ status: true, type: 'warning', msg: 'Selecione um evento para registrar frequência.' });
            return;
        }

        setRegisteringFrequency(true);
        try {
            const [printResult, frequencyResult] = await Promise.allSettled([
                handlePrintBatch(),
                registerFrequencyForSelected()
            ]);

            if (frequencyResult.status === 'fulfilled') {
                const { successIds, failedIds } = frequencyResult.value;
                if (failedIds.length > 0) {
                    setStateAlert({ status: true, type: 'warning', msg: `PDF processado. Frequência registrada para ${successIds.length} inscrito(s), ${failedIds.length} falha(s).` });
                } else {
                    setStateAlert({ status: true, type: 'success', msg: `PDF processado e ${successIds.length} frequência(s) registrada(s) com sucesso.` });
                }
            } else if (printResult.status === 'fulfilled') {
                setStateAlert({ status: true, type: 'warning', msg: 'PDF processado, mas não foi possível registrar as frequências.' });
            }
        } catch (error) {
            console.error('Erro ao registrar frequência antes da impressão:', error);
            setStateAlert({ status: true, type: 'warning', msg: 'PDF processado, mas não foi possível registrar as frequências.' });
        } finally {
            setRegisteringFrequency(false);
        }
    }

    async function handlePrintBatch() {
        if (selectedIds.length === 0) {
            setStateAlert({ status: true, type: 'warning', msg: 'Selecione ao menos um inscrito para imprimir.' });
            return;
        }

        setPrintingBatch(true);
        
        try {
            // Enviar todos os IDs de uma vez para gerar um único PDF
            const copiesCount = Number(copies) || 1;
            const response = await api.post(`order/selected-labels`, {
                subscribers: selectedIds,
                copies: copiesCount,
                ...(isRawLabelPrintingEnvironment() ? { printer } : {}),
            });

            if (response.status === 200) {
                if (response.data.bat) {
                    await markBatchAsPrinted(selectedIds);
                    downloadBase64File(response.data.bat, 'etiquetas-selecionadas.bat', 'application/octet-stream');
                    setStateAlert({ status: true, type: 'success', msg: `Arquivo de impressão gerado com ${selectedIds.length * copiesCount} crachá(s).` });
                    return;
                }

                const base64Pdf = response.data.pdf;
                const eventFileName = (eventSelected || 'selecionadas')
                    .replace(/[^a-z0-9]+/gi, '-')
                    .replace(/^-|-$/g, '')
                    .toLowerCase();

                downloadBase64File(
                    base64Pdf,
                    `etiquetas-${eventFileName || 'selecionadas'}.pdf`,
                    'application/pdf'
                );

                const byteCharacters = atob(base64Pdf);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                
                await markBatchAsPrinted(selectedIds);
                setPdfData(url);
                setOpenPrintModal(true);
                setStateAlert({ status: true, type: 'success', msg: `PDF gerado com ${selectedIds.length * copiesCount} crachá(s)!` });
            }
        } catch (error) {
            console.error('Erro ao gerar PDF em lote:', error);
            setStateAlert({ status: true, type: 'error', msg: `Erro ao gerar PDF: ${error.message}` });
        } finally {
            setPrintingBatch(false);
        }
    }

    async function loadMoreEvents() {
        if (!hasMoreEvents || loadingEvents) return;

        setLoadingEvents(true);
        try {
            const nextPage = pageEvents.current + 1;
            await getEvents(nextPage, 10, false);
            pageEvents.current = nextPage;

            if (nextPage >= pagination.pageCount) {
                setHasMoreEvents(false);
            }
        } catch (error) {
            console.error('Erro ao carregar eventos:', error);
        } finally {
            setLoadingEvents(false);
        }
    }

    async function loadInitialEvents() {
        setLoadingEvents(true);
        try {
            await getEvents(1, 10, true);
            pageEvents.current = 1;
            setHasMoreEvents(true);
            eventIdsRef.current.clear();
        } catch (error) {
            console.error('Erro ao carregar eventos iniciais:', error);
        } finally {
            setLoadingEvents(false);
        }
    }

    async function searchEventsByText(searchText) {
        if (!searchText || searchText.trim().length < 2) {
            loadInitialEvents();
            return;
        }

        setLoadingEvents(true);
        try {
            const query = qs.stringify({
                filters: {
                    deleted: { $eq: false },
                    name: { $containsi: searchText }
                },
                pagination: { page: 1, pageSize: 20 },
                fields: ['name', 'city', 'count_subscribers', 'max_subscribers', 'date_initial', 'end_subscription', 'isActive', 'counter_hostings', 'typeForm'],
                populate: {
                    plans: { populate: '*' },
                    hostings: { populate: '*' },
                    collectors: { populate: '*' }
                },
                sort: ['createdAt:desc'],
                encodeValuesOnly: true,
            });

            const response = await api.get(`/events?${query}`);
            if (response.status === 200) {
                const formattedEvents = response.data.data.map((event) => ({
                    ...event,
                    attributes: {
                        ...event.attributes,
                        count_subscribers: `${event.attributes.count_subscribers}`,
                        isActive: event.attributes.isActive === true ? 'Aberto' : 'Encerrado'
                    }
                }));

                eventIdsRef.current.clear();
                formattedEvents.forEach(event => eventIdsRef.current.add(event.id));
                setEventOptions(formattedEvents);
                pageEvents.current = 1;
                setHasMoreEvents(response.data.meta.pagination.pageCount > 1);
            }
        } catch (error) {
            console.error('Erro ao buscar eventos:', error);
        } finally {
            setLoadingEvents(false);
        }
    }

    useEffect(() => {
        async function loadInitialEvents() {
            setLoadingEvents(true);
            try {
                await getEvents(1, 10, true);
                pageEvents.current = 1;
                setHasMoreEvents(true);
                eventIdsRef.current.clear();
                await loadCurrentUserRole();
                await loadAuthorizationCode();
            } catch (error) {
                console.error('Erro ao carregar eventos:', error);
            } finally {
                setLoadingEvents(false);
            }
        }
        loadInitialEvents();

        return () => clearTimeout(eventSearchTimer.current);
    }, []);

    useEffect(() => {
        if (events && events.length > 0) {
            const newEvents = events.filter(event => !eventIdsRef.current.has(event.id));
            if (newEvents.length > 0) {
                newEvents.forEach(event => eventIdsRef.current.add(event.id));
                setEventOptions(prev => {
                    const existingIds = new Set(prev.map(event => event.id));
                    return [...prev, ...newEvents.filter(event => !existingIds.has(event.id))];
                });
            }
        }
    }, [events]);

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#f5f5f5' }} p={3}>
            <Snackbar
                open={stateAlert.status}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => setStateAlert({ status: false, type: 'success', msg: '' })}
            >
                <Alert sx={{ width: '100%' }} severity={stateAlert.type}>
                    {stateAlert.msg}
                </Alert>
            </Snackbar>

            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Box
                    sx={{
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap'
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Emissão de Crachás
                    </Typography>
                    {isAuthenticatedRoleUser ? (
                        <Button variant="outlined" onClick={() => setOpenManageCodeModal(true)}>
                            Cadastrar/Atualizar Código
                        </Button>
                    ) : null}
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={10} md={10}>
                        <FormControl fullWidth>
                            <Autocomplete
                                id="select-event-infinite-scroll"
                                options={eventOptions}
                                loading={loadingEvents}
                                getOptionLabel={(option) => option.attributes?.name || ''}
                                value={eventOptions.find(e => e.attributes?.name === eventSelected) || null}
                                onChange={(_, newValue) => {
                                    if (newValue) {
                                        setEventSelected(newValue.attributes.name);
                                        setEventSelectedId(newValue.id);
                                        setSubscribers([]);
                                        handleClearFilters();
                                    } else {
                                        setEventSelected(null);
                                        setEventSelectedId(null);
                                        setSubscribers([]);
                                        handleClearFilters();
                                    }
                                }}
                                onInputChange={(_, value, reason) => {
                                    if (reason === 'input') {
                                        setEventSearchText(value);
                                        clearTimeout(eventSearchTimer.current);
                                        eventSearchTimer.current = setTimeout(() => {
                                            searchEventsByText(value);
                                        }, 800);
                                    }
                                }}
                                filterOptions={(options) => options}
                                onOpen={() => {
                                    if (eventOptions.length === 0) {
                                        loadMoreEvents();
                                    }
                                }}
                                ListboxProps={{
                                    onScroll: (event) => {
                                        const listboxNode = event.currentTarget;
                                        if (
                                            listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10 &&
                                            hasMoreEvents &&
                                            !loadingEvents &&
                                            (!eventSearchText || eventSearchText.trim().length < 2)
                                        ) {
                                            loadMoreEvents();
                                        }
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Selecionar Evento *"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loadingEvents ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2} md={2}>
                        <TextField
                            fullWidth
                            type="text"
                            label="Número de cópias"
                            value={copies}
                            onChange={(e) => handleCopiesChange(e.target.value)}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 2 }}
                            helperText="Aplicado por inscrito"
                            disabled={!eventSelected}
                        />
                    </Grid>
                    {isRawLabelPrintingEnvironment() && (
                        <Grid item xs={12} sm={2} md={2}>
                            <TextField
                                fullWidth
                                select
                                label="Impressora"
                                value={printer}
                                onChange={(event) => setPrinter(event.target.value)}
                                disabled={!eventSelected}
                                SelectProps={{ native: true }}
                            >
                                <option value="ZEBRA">Zebra</option>
                                <option value="ELGIN">Elgin</option>
                            </TextField>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="CPF do Inscrito"
                            variant="outlined"
                            value={searchCpf}
                            onChange={(e) => {
                                const value = e.target.value;
                                const masked = maskCPF2(value);
                                if (masked.length <= 14) {
                                    setSearchCpf(masked);
                                }
                            }}
                            placeholder="000.000.000-00"
                            disabled={!eventSelected}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                            inputProps={{ maxLength: 14 }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Nome do Inscrito"
                            variant="outlined"
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            placeholder="Digite o nome"
                            disabled={!eventSelected}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="CNPJ da Empresa"
                            variant="outlined"
                            value={searchCnpj}
                            onChange={(e) => {
                                const value = e.target.value;
                                const masked = maskCNPJ(value);
                                if (masked.length <= 18) {
                                    setSearchCnpj(masked);
                                }
                            }}
                            placeholder="00.000.000/0000-00"
                            disabled={!eventSelected}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                            inputProps={{ maxLength: 18 }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Nome da Empresa"
                            variant="outlined"
                            value={searchCompanyName}
                            onChange={(e) => setSearchCompanyName(e.target.value)}
                            placeholder="Razão social ou fantasia"
                            disabled={!eventSelected}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showOnlyPrinted}
                                    onChange={(e) => setShowOnlyPrinted(e.target.checked)}
                                    disabled={!eventSelected}
                                />
                            }
                            label="Apenas impressos"
                            sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
                        />
                    </Grid>

                    

                    <Grid item xs={12}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleClearFilters}
                                disabled={loading || !eventSelected}
                            >
                                Limpar Filtros
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SearchIcon />}
                                onClick={handleSearch}
                                disabled={loading || !eventSelected}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Buscar'}
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '30vh' }}>
                    <CircularProgress size={60} />
                </Box>
            ) : subscribers.length > 0 ? (
                <Paper elevation={3} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Inscritos Encontrados ({subscribers.length})
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleSelectAll}
                                disabled={selectedIds.length === subscribers.length}
                            >
                                Selecionar Todos
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleDeselectAll}
                                disabled={selectedIds.length === 0}
                            >
                                Desmarcar Todos
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={printingBatch ? null : <PrintIcon />}
                                onClick={handlePrintBatch}
                                disabled={selectedIds.length === 0 || printingBatch || registeringFrequency}
                            >
                                {printingBatch ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    `Imprimir Selecionados (${selectedIds.length})`
                                )}
                            </Button>
                            <Button
                                variant="contained"
                                color="info"
                                onClick={handleRegisterFrequency}
                                disabled={selectedIds.length === 0 || registeringFrequency || printingBatch}
                            >
                                {registeringFrequency ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    `Registrar Frequência (${selectedIds.length})`
                                )}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={registeringFrequency || printingBatch ? null : <PrintIcon />}
                                onClick={handlePrintAndRegisterFrequency}
                                disabled={selectedIds.length === 0 || registeringFrequency || printingBatch}
                            >
                                {registeringFrequency || printingBatch ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    `Imprimir e Realizar Frequência (${selectedIds.length})`
                                )}
                            </Button>
                        </Stack>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={2}>
                        {subscribers.map((subscriber) => (
                            <Grid item xs={12} key={subscriber.id}>
                                <Paper
                                    elevation={1}
                                    sx={{
                                        p: 2,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        backgroundColor: subscriber.orderStatus === 'CANCELADA' ? '#ffebee' : (subscriber.printed ? '#fff3e0' : '#ffffff'),
                                        borderLeft: subscriber.orderStatus === 'CANCELADA' ? '4px solid #f44336' : (subscriber.printed ? '4px solid #ff9800' : 'none'),
                                        '&:hover': {
                                            backgroundColor: subscriber.orderStatus === 'CANCELADA' ? '#ffcdd2' : (subscriber.printed ? '#ffe0b2' : '#f9f9f9'),
                                            boxShadow: 3
                                        },
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={6} md="auto">
                                            <Checkbox
                                                checked={selectedIds.includes(subscriber.id)}
                                                onChange={() => handleToggleSelect(subscriber.id)}
                                                color="primary"
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6} md={2.5}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Nome
                                            </Typography>
                                            <Typography variant="body1" fontWeight={500}>
                                                {subscriber.name}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={2}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                CPF
                                            </Typography>
                                            <Typography variant="body2">
                                                {subscriber.document}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={1.5}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Crachá
                                            </Typography>
                                            <Typography variant="body2">
                                                {subscriber.badge}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={2.5}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Empresa
                                            </Typography>
                                            <Typography variant="body2" noWrap>
                                                {subscriber.company}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={1}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Cidade/UF
                                            </Typography>
                                            <Typography variant="body2">
                                                {subscriber.city}/{subscriber.state}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={1.5}>
                                            {subscriber.orderStatus === 'CANCELADA' && (
                                                <Typography 
                                                    variant="caption" 
                                                    color="error.main" 
                                                    sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
                                                >
                                                    ✗ CANCELADA
                                                </Typography>
                                            )}
                                            {subscriber.printed && subscriber.orderStatus !== 'CANCELADA' && (
                                                <Typography
                                                    variant="caption"
                                                    color="info.main"
                                                    sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}
                                                >
                                                    ✓ Já impresso
                                                </Typography>
                                            )}
                                            {registeredIds.has(subscriber.id) && subscriber.orderStatus !== 'CANCELADA' && (
                                                <Typography
                                                    variant="caption"
                                                    color="success.main"
                                                    sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}
                                                >
                                                    ✓ Já registrado
                                                </Typography>
                                            )}
                                            <Button
                                                variant="contained"
                                                color={subscriber.orderStatus === 'CANCELADA' ? "error" : (subscriber.printed ? "warning" : "success")}
                                                startIcon={printingId === subscriber.id ? null : <PrintIcon />}
                                                onClick={() => handlePrintLabel(subscriber.id)}
                                                disabled={printingId === subscriber.id}
                                                fullWidth
                                                size="small"
                                            >
                                                {printingId === subscriber.id ? (
                                                    <CircularProgress size={20} color="inherit" />
                                                ) : subscriber.orderStatus === 'CANCELADA' ? (
                                                    'Imprimir'
                                                ) : subscriber.printed ? (
                                                    'Reimprimir'
                                                ) : (
                                                    'Imprimir'
                                                )}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            ) : (
                <Paper elevation={3} sx={{ p: 5, textAlign: 'center' }}>
                    <Typography variant="h6" color="textSecondary">
                        {!eventSelected 
                            ? 'Selecione um evento para começar' 
                            : 'Utilize os filtros acima para buscar inscritos e emitir crachás'
                        }
                    </Typography>
                </Paper>
            )}

            {/* Modal de Impressão */}
            <Dialog
                open={openPrintModal}
                onClose={handleCloseModal}
                maxWidth="md"
                fullWidth
            >
                <DialogContent sx={{ p: 0, height: '80vh' }}>
                    {pdfData && (
                        <iframe
                            ref={printIframeRef}
                            src={pdfData}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none'
                            }}
                            title="Visualização do Crachá"
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="secondary">
                        Fechar
                    </Button>
                    <Button
                        onClick={handlePrint}
                        variant="contained"
                        color="primary"
                        startIcon={<PrintIcon />}
                    >
                        Imprimir
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de Autorização por Código */}
            <Dialog
                open={openAuthorizationModal}
                onClose={handleCancelAuthorizationModal}
                maxWidth="xs"
                fullWidth
            >
                <DialogContent>
                    <Typography variant="h6" gutterBottom sx={{ color: '#f57c00', fontWeight: 600 }}>
                        ⚠️ Autorização Necessária
                    </Typography>
                    <Typography variant="body2" gutterBottom sx={{ mb: 3, color: 'text.secondary' }}>
                        {(() => {
                            const subscriber = subscribers.find(s => s.id === pendingPrintId);
                            if (subscriber?.orderStatus === 'CANCELADA') {
                                return 'Este pedido está CANCELADO. Para imprimir o crachá, é necessário autorização do administrador.';
                            }
                            return 'Este crachá já foi impresso anteriormente. Para reimprimir, é necessário autorização do administrador.';
                        })()}
                    </Typography>
                    <TextField
                        fullWidth
                        type="password"
                        label="Código Administrador"
                        variant="outlined"
                        value={adminCodeInput}
                        onChange={(e) => {
                            setAdminCodeInput(e.target.value);
                            setAuthorizationError('');
                        }}
                        error={!!authorizationError}
                        helperText={authorizationError}
                        autoFocus
                        sx={{ mb: 2 }}
                        autoComplete="new-password"
                        inputProps={{ autoComplete: 'new-password' }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                validateAdminCode();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCancelAuthorizationModal} 
                        color="inherit"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={validateAdminCode}
                        variant="contained"
                        color="primary"
                        disabled={!adminCodeInput}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openManageCodeModal} onClose={() => setOpenManageCodeModal(false)} fullWidth maxWidth="sm">
                <DialogTitle>Cadastrar/Atualizar Código de Reimpressão</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'grid', gap: 2, mt: 1 }} autoComplete="off">
                        {configuredAdminCode ? (
                            <TextField
                                label="Código Atual"
                                type="password"
                                value={currentCodeInput}
                                onChange={(e) => setCurrentCodeInput(e.target.value)}
                                autoComplete="new-password"
                                inputProps={{ autoComplete: 'new-password' }}
                            />
                        ) : null}
                        <TextField
                            label="Novo Código"
                            type="password"
                            value={newCodeInput}
                            onChange={(e) => setNewCodeInput(e.target.value)}
                            autoComplete="new-password"
                            inputProps={{ autoComplete: 'new-password' }}
                        />
                        <TextField
                            label="Confirmar Novo Código"
                            type="password"
                            value={confirmCodeInput}
                            onChange={(e) => setConfirmCodeInput(e.target.value)}
                            autoComplete="new-password"
                            inputProps={{ autoComplete: 'new-password' }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenManageCodeModal(false)} color="inherit">Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveAuthorizationCode}
                        disabled={savingCode}
                        startIcon={savingCode ? <CircularProgress size={16} /> : null}
                    >
                        Salvar Código
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
